import type { ReactiveString, RequestOptions } from "../types/request";
import type { ServiceName } from "../types/service"

/**
 * @typedef {Object} addArticleContentFormData Article content data
 * @prop {string} article_id - ID of the article this content is for
 * @prop {string} content_type - Type of content (Article Title | Image | Image Collection | Quote | Note | Paragraph | Small Note | Source | Subtitle | Title)
 * @prop {string} language - Language of the content
 * @prop {string=} deleted - Whether the content is deleted or not
 * @prop {Number} sequence_order - Position in which this content should appear in the associated article
 * @prop {string=} content - Content the Content Type is not an Image
 * @prop {string=} image_ref_id - ID of the Image reference in Image_references table if Content Type is Image
 * @prop {Number=} source_index - Position of the content if the Content Type is a source. Will show in superscript beside the Source
 */
export type addArticleContentFormData = {
	article_id: string;
	content_type: string;
	language: string;
	deleted?: string;
	sequence_order: Number;
	content?: string;
	image_ref_id?: string;
	source_index?: Number;
};

/**
 * @typedef {Object} editArticleContentFormData Article Content data
 * @prop {string=} content_type - Type of content (Article Title | Image | Image Collection | Quote | Note | Paragraph | Small Note | Source | Subtitle | Title)
 * @prop {string=} language - Language of the content
 * @prop {string=} deleted - Whether the content is deleted or not
 * @prop {Number=} sequence_order - Position in which this content should appear in the associated article
 * @prop {string=} content - Content the Content Type is not an Image
 * @prop {string=} image_ref_id - ID of the Image reference in Image_references table if Content Type is Image
 * @prop {Number=} source_index - Position of the content if the Content Type is a source. Will show in superscript beside the Source
 * @prop {string=} image_ids - File IDs of the Images if Content Type is Image Array (To be used with Service-SHRD-FM). IDs must be separated by a ';'
 */
export type editArticleContentFormData = {
	content_type?: string;
	language?: string;
	deleted?: string;
	sequence_order?: Number;
	content?: string;
	image_ref_id?: string;
	source_index?: Number;
	image_ids?: string;
};

/**
 * @typedef {Object} addArticleFormData Article data
 * @prop {string} platform_id - ID of the platform where the article comes from
 * @prop {string} publish_state - Publish state of the article (public|private)
 * @prop {string} date_of_publication - Date the article was published
 * @prop {string} author - Author of the article
 * @prop {string} banner_img - ID of the image to be on the banner (To be used with Service-SHRD-FM)
 * @prop {string} facebook_url - URL to share on Facebook
 * @prop {string} twitter_url - URL to share on Twitter
 * @prop {string} linkedin_url - URL to share on LinkedIn
 * @prop {string} page_name - Name of the page (for tab)
 * @prop {boolean} visibility - Whether the article should be visible or not
 * @prop {boolean=} deleted - Whether the article is deleted
 * @prop {string} page_link - Desired path name for the article page
 * @prop {string} category - Article's category
 * @prop {string=} summary - Article's summary
 */
export type addArticleFormData = {
	platform_id: string;
	publish_state: string;
	date_of_publication: string;
	author: string;
	banner_img: string;
	facebook_url: string;
	twitter_url: string;
	linkedin_url: string;
	page_name: string;
	visibility: boolean;
	deleted?: boolean;
	page_link: string;
	category: string;
	summary?: string;
};

/**
 * @typedef {Object} addAuthorizedMerchantFormData Authorized Merchant's data
 * @prop {string} platform_id - Platform Id
 * @prop {string} requestor_organization_id - Requestor Organization Id
 * @prop {string} requestor_user_id - Requestor User Id
 * @prop {string|null=} approver - Approver
 * @prop {Number|null=} approval_status - Approval Status
 * @prop {string|null=} decision_reason - Decision Reason
 * @prop {string} merchant_organization_id - Merchant's Organization Id
 * @prop {boolean} is_participant - is_participant
 * @prop {string|null=} joined_date - Date Joines
 * @prop {string|null=} removed_date - Date Removed
 * @prop {string|null=} blacklisted_date - Date Blacklisted
 */
export type addAuthorizedMerchantFormData = {
	platform_id: string;
	requestor_organization_id: string;
	requestor_user_id: string;
	approver?: string|null;
	approval_status?: Number|null;
	decision_reason?: string|null;
	merchant_organization_id: string;
	is_participant: boolean;
	joined_date?: string|null;
	removed_date?: string|null;
	blacklisted_date?: string|null;
};

/**
 * @typedef {Object} editAuthorizedMerchantFormData Authorized Merchant's data
 * @prop {string=} platform_id - Platform Id
 * @prop {string=} requestor_organization_id - Requestor Organization Id
 * @prop {string=} requestor_user_id - Requestor User Id
 * @prop {string|null=} approver - Approver
 * @prop {Number|null=} approval_status - Approval Status
 * @prop {string|null=} decision_reason - Decision Reason
 * @prop {string=} merchant_organization_id - Merchant's Organization Id
 * @prop {boolean=} is_participant - is_participant
 * @prop {string|null=} joined_date - Date Joines
 * @prop {string|null=} removed_date - Date Removed
 * @prop {string|null=} blacklisted_date - Date Blacklisted
 */
export type editAuthorizedMerchantFormData = {
	platform_id?: string;
	requestor_organization_id?: string;
	requestor_user_id?: string;
	approver?: string|null;
	approval_status?: Number|null;
	decision_reason?: string|null;
	merchant_organization_id?: string;
	is_participant?: boolean;
	joined_date?: string|null;
	removed_date?: string|null;
	blacklisted_date?: string|null;
};

/**
 * @typedef {Object} listAuthorizedPartnersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listAuthorizedPartnersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addAuthorizedPartnerFormData Authorized Partner's data
 * @prop {string} platform_id - Platform Id
 * @prop {string} partner_organization_id - Partner Organization Id
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addAuthorizedPartnerFormData = {
	platform_id: string;
	partner_organization_id: string;
	status: Number;
};

/**
 * @typedef {Object} editAuthorizedPartnerFormData Authorized Partner's data
 * @prop {string=} platform_id - Platform Id
 * @prop {string=} partner_organization_id - Partner Organization Id
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editAuthorizedPartnerFormData = {
	platform_id?: string;
	partner_organization_id?: string;
	status?: Number;
};

/**
 * @typedef {Object} newBannerFormData Banner data
 * @prop {Number=} model - Model number of the Banner
 * @prop {string=} background_url - URL or ID (to use with SERVICE-SHRD-FM) of the background of the banner
 * @prop {string=} background_color - Color of the banner (in desktop version)
 * @prop {string=} background_position - Position of the banner image (in desktop version)
 * @prop {string=} pre_title - Pre-Title content in the banner
 * @prop {string=} title - Title content in the banner
 * @prop {string=} image1 - URL or ID (to use with SERVICE-SHRD-FM) of the first image in the banner
 * @prop {string=} background_size - How the background image should cover the banner (cover|contain)
 * @prop {string} page_id - ID of the page this banner will be in
 * @prop {string=} category_icon - File ID (to use with SERVICE-SHRD-FM) of the category icon 
 * @prop {string=} paragraph - Paragraph content in the banner
 * @prop {string=} button_text - Button text content of the button in the banner
 * @prop {string=} image2 - URL or ID (to use with SERVICE-SHRD-FM) of the first image in the banner
 * @prop {boolean=} deleted - Whether the Banner is deleted or not
 * @prop {string=} backgroundmobile_color - Color of the background in mobile version
 * @prop {string=} backgroundmobile_url - URL or ID (to use with SERVICE-SHRD-FM) of the background of the banner in the mobile version
 * @prop {string=} backgroundmobile_position - Position of the background of the banner in the mobile version
 * @prop {string=} backgroundmobile_size - How the background image should cover the banner in mobile version (cover|contain)
 * @prop {string=} slider_images - List of UUIDs of the images for a slider animation in the banner (separate with a ;)
 * @prop {string=} pre_logo - URL or ID (to use with SERVICE-SHRD-FM) of the pre-logo in the banner
 */
export type newBannerFormData = {
	model?: Number;
	background_url?: string;
	background_color?: string;
	background_position?: string;
	pre_title?: string;
	title?: string;
	image1?: string;
	background_size?: string;
	page_id: string;
	category_icon?: string;
	paragraph?: string;
	button_text?: string;
	image2?: string;
	deleted?: boolean;
	backgroundmobile_color?: string;
	backgroundmobile_url?: string;
	backgroundmobile_position?: string;
	backgroundmobile_size?: string;
	slider_images?: string;
	pre_logo?: string;
};

/**
 * @typedef {Object} editBannerFormData Banner data
 * @prop {Number=} model - Model number of the Banner
 * @prop {string=} background_url - URL or ID (to use with SERVICE-SHRD-FM) of the background of the banner
 * @prop {string=} background_color - Color of the banner (in desktop version)
 * @prop {string=} background_position - Position of the banner image (in desktop version)
 * @prop {string=} pre_title - Pre-Title content in the banner
 * @prop {string=} title - Title content in the banner
 * @prop {string=} image1 - URL or ID (to use with SERVICE-SHRD-FM) of the first image in the banner
 * @prop {string=} background_size - How the background image should cover the banner (cover|contain)
 * @prop {string=} category_icon - File ID (to use with SERVICE-SHRD-FM) of the category icon 
 * @prop {string=} paragraph - Paragraph content in the banner
 * @prop {string=} button_text - Button text content of the button in the banner
 * @prop {string=} image2 - URL or ID (to use with SERVICE-SHRD-FM) of the first image in the banner
 * @prop {boolean=} deleted - Whether the Banner is deleted or not
 * @prop {string=} backgroundmobile_color - Color of the background in mobile version
 * @prop {string=} backgroundmobile_url - URL or ID (to use with SERVICE-SHRD-FM) of the background of the banner in the mobile version
 * @prop {string=} backgroundmobile_position - Position of the background of the banner in the mobile version
 * @prop {string=} backgroundmobile_size - How the background image should cover the banner in mobile version (cover|contain)
 * @prop {string=} slider_images - IDs of the images for a slider animation in the banner (separate with a ;)
 * @prop {string=} pre_logo - URL or ID (to use with SERVICE-SHRD-FM) of the pre-logo in the banner
 */
export type editBannerFormData = {
	model?: Number;
	background_url?: string;
	background_color?: string;
	background_position?: string;
	pre_title?: string;
	title?: string;
	image1?: string;
	background_size?: string;
	category_icon?: string;
	paragraph?: string;
	button_text?: string;
	image2?: string;
	deleted?: boolean;
	backgroundmobile_color?: string;
	backgroundmobile_url?: string;
	backgroundmobile_position?: string;
	backgroundmobile_size?: string;
	slider_images?: string;
	pre_logo?: string;
};

/**
 * @typedef {Object} addBlockItemFormData Block item data
 * @prop {string} block_id - ID of the block this item is associated to
 * @prop {string=} pre_title - pre-title of the Block Item
 * @prop {string=} title - Title of the Block Item
 * @prop {string=} paragraph - Paragraph in the Block Item
 * @prop {string=} bottom_link - Bottom link in the Block Item
 * @prop {string=} footnote - Footnote in the Block Item
 * @prop {string=} image1 - File ID (to use with SERVICE-SHRD-FM) of the first image in the Block Item
 * @prop {string=} image2 - File ID (to use with SERVICE-SHRD-FM) of the second image in the Block Item
 * @prop {string=} text - Text within the Block Item
 * @prop {string=} button_text - Text in Button in the Block Item
 * @prop {string=} button_link - Link for button in  Block Item
 * @prop {string=} bolded_text - Bolded Text in Block item
 * @prop {string=} bolded_text_link - Link for bolded text in  Block Item
 * @prop {string=} logo_image - URL or ID (to use with SERVICE-SHRD-FM) of the logo in the block_item
 * @prop {boolean=} deleted - Whether the Banner is deleted or not
 * @prop {string=} quoted_text - Text in quotes in the Block Item
 * @prop {string=} subject_id - File ID (to use with SERVICE-SHRD-FM) of the subject image
 * @prop {Number} sequence_order - Order in the Sequence of Block items (of their respective Block) that this block item needs to appear.
 * @prop {Number=} model - Model number of the Block item
 * @prop {string=} layout - Layout of the Block Item
 * @prop {string=} icon - URL or ID (to use with SERVICE-SHRD-FM) of the icon in the block item
 * @prop {boolean} is_checklist - Determines if the block item is part of a checklist
 * @prop {string=} category - ID (to use with SERVICE-SHRD-FM) of category image in the block item
 * @prop {string=} logo_array - List of UUIDs of the images for a slider animation in the banner (separate with a ;)
 * @prop {string=} top_tag - Tag at top of the block item
 * @prop {string=} top_tag_color - Color of tag at top of the block item
 * @prop {string=} video_url - URL of video in the block item
 */
export type addBlockItemFormData = {
	block_id: string;
	pre_title?: string;
	title?: string;
	paragraph?: string;
	bottom_link?: string;
	footnote?: string;
	image1?: string;
	image2?: string;
	text?: string;
	button_text?: string;
	button_link?: string;
	bolded_text?: string;
	bolded_text_link?: string;
	logo_image?: string;
	deleted?: boolean;
	quoted_text?: string;
	subject_id?: string;
	sequence_order: Number;
	model?: Number;
	layout?: string;
	icon?: string;
	is_checklist: boolean;
	category?: string;
	logo_array?: string;
	top_tag?: string;
	top_tag_color?: string;
	video_url?: string;
};

/**
 * @typedef {Object} editBlockItemFormData Block item data
 * @prop {string=} pre_title - pre-title of the Block Item
 * @prop {string=} title - Title of the Block Item
 * @prop {string=} paragraph - Paragraph in the Block Item
 * @prop {string=} bottom_link - Bottom link in the Block Item
 * @prop {string=} footnote - Footnote in the Block Item
 * @prop {string=} image1 - File ID (to use with SERVICE-SHRD-FM) of the first image in the Block Item
 * @prop {string=} image2 - File ID (to use with SERVICE-SHRD-FM) of the second image in the Block Item
 * @prop {string=} text - Text within the Block Item
 * @prop {string=} button_text - Text in Button in the Block Item
 * @prop {string=} button_link - Link for button in  Block Item
 * @prop {string=} bolded_text - Bolded Text in Block item
 * @prop {string=} bolded_text_link - Link for bolded text in  Block Item
 * @prop {string=} logo_image - URL or ID (to use with SERVICE-SHRD-FM) of the logo in the block_item
 * @prop {boolean=} deleted - Whether the Banner is deleted or not
 * @prop {string=} quoted_text - Text in quotes in the Block Item
 * @prop {string=} subject_id - File ID (to use with SERVICE-SHRD-FM) of the subject image
 * @prop {Number} sequence_order - Order in the Sequence of Block items (of their respective Block) that this block item needs to appear.
 * @prop {Number=} model - Model number of the Block item
 * @prop {string=} layout - Layout of the Block Item
 * @prop {string=} icon - URL or ID (to use with SERVICE-SHRD-FM) of the icon in the block item
 * @prop {boolean} is_checklist - Determines if the block item is part of a checklist
 * @prop {string=} category - ID (to use with SERVICE-SHRD-FM) of category image in the block item
 * @prop {string=} logo_array - List of UUIDs of the images for a slider animation in the banner (separate with a ;)
 * @prop {string=} top_tag - Tag at top of the block item
 * @prop {string=} top_tag_color - Color of tag at top of the block item
 * @prop {string=} video_url - URL of video in the block item
 */
export type editBlockItemFormData = {
	pre_title?: string;
	title?: string;
	paragraph?: string;
	bottom_link?: string;
	footnote?: string;
	image1?: string;
	image2?: string;
	text?: string;
	button_text?: string;
	button_link?: string;
	bolded_text?: string;
	bolded_text_link?: string;
	logo_image?: string;
	deleted?: boolean;
	quoted_text?: string;
	subject_id?: string;
	sequence_order: Number;
	model?: Number;
	layout?: string;
	icon?: string;
	is_checklist: boolean;
	category?: string;
	logo_array?: string;
	top_tag?: string;
	top_tag_color?: string;
	video_url?: string;
};

/**
 * @typedef {Object} addBlockFormData Block data
 * @prop {string} block_name - Name of the block we are creating
 * @prop {Number} sequence_order - Order in the Sequence of Blocks (of their respective Section) that this block needs to appear.
 * @prop {string=} layout - Layout of this block
 * @prop {Number=} columns - Number of columns in this block
 * @prop {boolean=} publish_state - Whether the block is published or not (hidden)
 * @prop {Number=} model - Model Number of the Block
 * @prop {boolean=} deleted - Whether the Block is deleted or not
 */
export type addBlockFormData = {
	block_name: string;
	sequence_order: Number;
	layout?: string;
	columns?: Number;
	publish_state?: boolean;
	model?: Number;
	deleted?: boolean;
};

/**
 * @typedef {Object} editBlockFormData Block data
 * @prop {string=} block_name - Name of the block we are creating
 * @prop {Number=} sequence_order - Order in the Sequence of Blocks (of their respective Section) that this block needs to appear.
 * @prop {string=} layout - Layout of this block
 * @prop {Number=} columns - Number of columns in this block
 * @prop {boolean=} publish_state - Whether the block is published or not (hidden)
 * @prop {Number=} model - Model Number of the Block
 * @prop {boolean=} deleted - Whether the Block is deleted or not
 */
export type editBlockFormData = {
	block_name?: string;
	sequence_order?: Number;
	layout?: string;
	columns?: Number;
	publish_state?: boolean;
	model?: Number;
	deleted?: boolean;
};

/**
 * @typedef {Object} addComponentFormData Component data
 * @prop {string} page_id - ID of the page this component will be part of
 * @prop {string=} component_parent_id - ID of the parent component, leave blank if this is the parent.
 * @prop {string} component_name - Name of the component.
 * @prop {Number=} sequence_order - Where the component is in the list of components (as a parent or child)
 * @prop {string=} props - JSON string of props for the component
 */
export type addComponentFormData = {
	page_id: string;
	component_parent_id?: string;
	component_name: string;
	sequence_order?: Number;
	props?: string;
};

/**
 * @typedef {Object} editComponentFormData Component data
 * @prop {string} page_id - ID of the page this component will be part of
 * @prop {string=} component_parent_id - ID of the parent component, leave blank if this is the parent.
 * @prop {string} component_name - Name of the component.
 * @prop {Number=} sequence_order - Where the component is in the list of components (as a parent or child)
 * @prop {string=} props - JSON string of props for the component
 */
export type editComponentFormData = {
	page_id: string;
	component_parent_id?: string;
	component_name: string;
	sequence_order?: Number;
	props?: string;
};

/**
 * @typedef {Object} listComposedElementsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listComposedElementsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addComposedElementFormData Composed Element's data
 * @prop {string} group_element_id - Group Element Id
 * @prop {string} type - Type
 * @prop {Number} sequence - Sequence
 * @prop {string} name - Name
 * @prop {string|null=} description - Description
 * @prop {string} label - Label
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {string|null=} text - Text
 * @prop {string|null=} url - Url
 * @prop {boolean|null=} version - Version
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addComposedElementFormData = {
	group_element_id: string;
	type: string;
	sequence: Number;
	name: string;
	description?: string|null;
	label: string;
	translation_slug?: string|null;
	text?: string|null;
	url?: string|null;
	version?: boolean|null;
	status: Number;
};

/**
 * @typedef {Object} editComposedElementFormData Composed Element's data
 * @prop {string=} group_element_id - Group Element Id
 * @prop {string=} type - Type
 * @prop {Number=} sequence - Sequence
 * @prop {string=} name - Name
 * @prop {string|null=} description - Description
 * @prop {string=} label - Label
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {string|null=} text - Text
 * @prop {string|null=} url - Url
 * @prop {boolean|null=} version - Version
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editComposedElementFormData = {
	group_element_id?: string;
	type?: string;
	sequence?: Number;
	name?: string;
	description?: string|null;
	label?: string;
	translation_slug?: string|null;
	text?: string|null;
	url?: string|null;
	version?: boolean|null;
	status?: Number;
};

/**
 * @typedef {Object} addImageCollectionFormData Article data
 * @prop {string} article_content_id - ID of the article content this image will be part of
 * @prop {string=} collection_subtitle - Subtitle under the image collection (optionnal)
 */
export type addImageCollectionFormData = {
	article_content_id: string;
	collection_subtitle?: string;
};

/**
 * @typedef {Object} editImageCollectionFormData Article data
 * @prop {string} article_content_id - ID of the article content this image will be part of
 * @prop {string=} collection_subtitle - Subtitle under the image collection (optionnal)
 */
export type editImageCollectionFormData = {
	article_content_id: string;
	collection_subtitle?: string;
};

/**
 * @typedef {Object} addImageReferenceFormData Image Reference's data
 * @prop {string} image_id - File ID (to use with SERVICE-SHRD-FM) of the image
 * @prop {string=} image_subtitle - Image Subtitle
 * @prop {string=} collection_id - Collection Id if part of an image collection
 * @prop {Number=} sequence_order - Sequence Order if part of an image collection
 */
export type addImageReferenceFormData = {
	image_id: string;
	image_subtitle?: string;
	collection_id?: string;
	sequence_order?: Number;
};

/**
 * @typedef {Object} editImageReferenceFormData Image Reference's data
 * @prop {string=} image_id - File ID (to use with SERVICE-SHRD-FM) of the image
 * @prop {string=} image_subtitle - Image Subtitle
 * @prop {string=} collection_id - Collection Id if part of an image collection
 * @prop {Number=} sequence_order - Sequence Order if part of an image collection
 */
export type editImageReferenceFormData = {
	image_id?: string;
	image_subtitle?: string;
	collection_id?: string;
	sequence_order?: Number;
};

/**
 * @typedef {Object} makeRequestOrInvitationMarketplaceFormData Article data
 * @prop {string} platform_id - The ID of the platform to which the request is made.
 * @prop {string} merchant_organization_id - The ID of the merchant organization who’s making the request.
 * @prop {boolean=} is_participant - Where the merchant for this entry is a merchant that all new textiles are requested to be added to the marketplace. They are different because the merchant is an organization member of the owning aggregator, ex an association and their members.
 * @prop {string} requestor_organization_id - The ID of the merchant organization which is making the request or the invitation.
 * @prop {string} requestor_user_id - The ID of the requesting user (Merchant, aggregator) who is making the actual request or invitation.
 * @prop {Number} approval_status - The ID of the merchant organization to get the available marketplaces list for.
 */
export type makeRequestOrInvitationMarketplaceFormData = {
	platform_id: string;
	merchant_organization_id: string;
	is_participant?: boolean;
	requestor_organization_id: string;
	requestor_user_id: string;
	approval_status: Number;
};

/**
 * @typedef {Object} listActiveMerchantsMarketplaceFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listActiveMerchantsMarketplaceFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} updateRequestOrInvitationMarketplaceFormData Update Authorization Request Data
 * @prop {Number} approval_status - Approval Status (1: Approve, 2: Decline, 3: Removed, 4: Blacklisted)
 * @prop {string|null=} decision_reason
 */
export type updateRequestOrInvitationMarketplaceFormData = {
	approval_status: Number;
	decision_reason?: string|null;
};

/**
 * @typedef {Object} listPageMetasFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPageMetasFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPageMetaFormData Page Meta's data
 * @prop {string} platform_id - Platform Id
 * @prop {string|null=} page_id - Page Id
 * @prop {string|null=} language_id - Language Id
 * @prop {string|null=} title - Title
 * @prop {string|null=} description - Description
 * @prop {string|null=} image_id - Image Id
 */
export type addPageMetaFormData = {
	platform_id: string;
	page_id?: string|null;
	language_id?: string|null;
	title?: string|null;
	description?: string|null;
	image_id?: string|null;
};

/**
 * @typedef {Object} editPageMetaFormData Page Meta's data
 * @prop {string=} platform_id - Platform Id
 * @prop {string|null=} page_id - Page Id
 * @prop {string|null=} language_id - Language Id
 * @prop {string|null=} title - Title
 * @prop {string|null=} description - Description
 * @prop {string|null=} image_id - Image Id
 */
export type editPageMetaFormData = {
	platform_id?: string;
	page_id?: string|null;
	language_id?: string|null;
	title?: string|null;
	description?: string|null;
	image_id?: string|null;
};

/**
 * @typedef {Object} listPageStatesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPageStatesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPageStateFormData Page State's data
 * @prop {string} platform_id - Platform Id
 * @prop {string|null=} page_id - Page Id
 * @prop {boolean|null=} availability - Availability
 * @prop {boolean|null=} published_state - Published State
 */
export type addPageStateFormData = {
	platform_id: string;
	page_id?: string|null;
	availability?: boolean|null;
	published_state?: boolean|null;
};

/**
 * @typedef {Object} editPageStateFormData Page State's data
 * @prop {string=} platform_id - Platform Id
 * @prop {string|null=} page_id - Page Id
 * @prop {boolean|null=} availability - Availability
 * @prop {boolean|null=} published_state - Published State
 */
export type editPageStateFormData = {
	platform_id?: string;
	page_id?: string|null;
	availability?: boolean|null;
	published_state?: boolean|null;
};

/**
 * @typedef {Object} listPagesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPagesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPageFormData Page's data
 * @prop {string} page_name - Page Name
 * @prop {boolean|null=} publish_state - Publish State
 * @prop {string|null=} meta_name - Meta Name
 * @prop {string|null=} meta_description - Meta Description
 * @prop {boolean|null=} deleted - Deleted
 * @prop {string|null=} template_item_id - Template Item Id
 * @prop {boolean|null=} toggable_visibility - Toggable Visibility
 * @prop {string|null=} last_update - Last Update
 * @prop {string|null=} platform_id - Platform Id
 */
export type addPageFormData = {
	page_name: string;
	publish_state?: boolean|null;
	meta_name?: string|null;
	meta_description?: string|null;
	deleted?: boolean|null;
	template_item_id?: string|null;
	toggable_visibility?: boolean|null;
	last_update?: string|null;
	platform_id?: string|null;
};

/**
 * @typedef {Object} listPlatformBrandingsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformBrandingsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlatformBrandingFormData Platform Branding's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string|null=} logo - Logo
 * @prop {string|null=} favicon_id - Favicon Id
 * @prop {string|null=} style - Style
 * @prop {string|null=} signin_photo - Signin Photo
 * @prop {string|null=} custom_standard_titlebar_msg - Custom Standard Titlebar Msg
 * @prop {string|null=} custom_checkout_titlebar_msg - Custom Checkout Titlebar Msg
 * @prop {string|null=} platform_description - Description of the platform
 * @prop {string|null=} brand_awareness_type - Brand awareness chosen for marketplaces
 */
export type addPlatformBrandingFormData = {
	platform_id?: string|null;
	logo?: string|null;
	favicon_id?: string|null;
	style?: string|null;
	signin_photo?: string|null;
	custom_standard_titlebar_msg?: string|null;
	custom_checkout_titlebar_msg?: string|null;
	platform_description?: string|null;
	brand_awareness_type?: string|null;
};

/**
 * @typedef {Object} editPlatformBrandingFormData Platform Branding's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string|null=} logo - Logo
 * @prop {string|null=} favicon_id - Favicon Id
 * @prop {string|null=} style - Style
 * @prop {string|null=} signin_photo - Signin Photo
 * @prop {string|null=} custom_standard_titlebar_msg - Custom Standard Titlebar Msg
 * @prop {string|null=} custom_checkout_titlebar_msg - Custom Checkout Titlebar Msg
 * @prop {string|null=} platform_description - Description of the platform
 * @prop {string|null=} brand_awareness_type - Brand awareness chosen for marketplaces
 */
export type editPlatformBrandingFormData = {
	platform_id?: string|null;
	logo?: string|null;
	favicon_id?: string|null;
	style?: string|null;
	signin_photo?: string|null;
	custom_standard_titlebar_msg?: string|null;
	custom_checkout_titlebar_msg?: string|null;
	platform_description?: string|null;
	brand_awareness_type?: string|null;
};

/**
 * @typedef {Object} listPlatformColorsetsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformColorsetsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlatformColorsetFormData Platform Colorset's data
 * @prop {string|null=} platform_branding_id - Platform Branding Id
 * @prop {string|null=} titles - Titles
 * @prop {string|null=} subtitles - Subtitles
 * @prop {string|null=} standard_text - Standard Text
 * @prop {string|null=} links - Links
 * @prop {string|null=} custom_titlebar_background - Custom Titlebar Background
 * @prop {string|null=} custom_titlebar_text - Custom Titlebar Text
 */
export type addPlatformColorsetFormData = {
	platform_branding_id?: string|null;
	titles?: string|null;
	subtitles?: string|null;
	standard_text?: string|null;
	links?: string|null;
	custom_titlebar_background?: string|null;
	custom_titlebar_text?: string|null;
};

/**
 * @typedef {Object} editPlatformColorsetFormData Platform Colorset's data
 * @prop {string|null=} platform_branding_id - Platform Branding Id
 * @prop {string|null=} titles - Titles
 * @prop {string|null=} subtitles - Subtitles
 * @prop {string|null=} standard_text - Standard Text
 * @prop {string|null=} links - Links
 * @prop {string|null=} custom_titlebar_background - Custom Titlebar Background
 * @prop {string|null=} custom_titlebar_text - Custom Titlebar Text
 */
export type editPlatformColorsetFormData = {
	platform_branding_id?: string|null;
	titles?: string|null;
	subtitles?: string|null;
	standard_text?: string|null;
	links?: string|null;
	custom_titlebar_background?: string|null;
	custom_titlebar_text?: string|null;
};

/**
 * @typedef {Object} listPlatformConfigsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformConfigsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlatformConfigFormData Platform Config's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string|null=} name - Name
 * @prop {string|null=} url - Url
 * @prop {string|null=} domain - Domain
 * @prop {Number|null=} domain_confirmed - Domain Confirmed
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string|null=} email_sender_address - Email Sender Address
 * @prop {string|null=} email_replyTo_address - Email reply_to Address
 * @prop {string|null=} google_site_verification - Verification ID for SEO
 */
export type addPlatformConfigFormData = {
	platform_id?: string|null;
	name?: string|null;
	url?: string|null;
	domain?: string|null;
	domain_confirmed?: Number|null;
	contact_id?: string|null;
	email_sender_address?: string|null;
	email_replyTo_address?: string|null;
	google_site_verification?: string|null;
};

/**
 * @typedef {Object} editPlatformConfigFormData Platform Config's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string|null=} name - Name
 * @prop {string|null=} url - Url
 * @prop {string|null=} domain - Domain
 * @prop {Number|null=} domain_confirmed - Domain Confirmed
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string|null=} email_sender_address - Email Sender Address
 * @prop {string|null=} email_replyTo_address - Email reply_to Address
 * @prop {string|null=} google_site_verification - Verification ID for SEO
 */
export type editPlatformConfigFormData = {
	platform_id?: string|null;
	name?: string|null;
	url?: string|null;
	domain?: string|null;
	domain_confirmed?: Number|null;
	contact_id?: string|null;
	email_sender_address?: string|null;
	email_replyTo_address?: string|null;
	google_site_verification?: string|null;
};

/**
 * @typedef {Object} listPlatformFontsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformFontsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlatformFontFormData Platform Font's data
 * @prop {string|null=} platform_branding_id - Platform Branding Id
 * @prop {string|null=} primary_font - Primary Font
 * @prop {string|null=} secondary_font - Secondary Font
 */
export type addPlatformFontFormData = {
	platform_branding_id?: string|null;
	primary_font?: string|null;
	secondary_font?: string|null;
};

/**
 * @typedef {Object} editPlatformFontFormData Platform Font's data
 * @prop {string|null=} platform_branding_id - Platform Branding Id
 * @prop {string|null=} primary_font - Primary Font
 * @prop {string|null=} secondary_font - Secondary Font
 */
export type editPlatformFontFormData = {
	platform_branding_id?: string|null;
	primary_font?: string|null;
	secondary_font?: string|null;
};

/**
 * @typedef {Object} listPlatformInputsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformInputsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlatformInputFormData Platform Input's data
 * @prop {string|null=} platform_branding_id - Platform Branding Id
 * @prop {string|null=} shape - Shape
 * @prop {string|null=} primary_color - Primary Color
 */
export type addPlatformInputFormData = {
	platform_branding_id?: string|null;
	shape?: string|null;
	primary_color?: string|null;
};

/**
 * @typedef {Object} editPlatformInputFormData Platform Input's data
 * @prop {string|null=} platform_branding_id - Platform Branding Id
 * @prop {string|null=} shape - Shape
 * @prop {string|null=} primary_color - Primary Color
 */
export type editPlatformInputFormData = {
	platform_branding_id?: string|null;
	shape?: string|null;
	primary_color?: string|null;
};

/**
 * @typedef {Object} listPlatformsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlatformFormData Platform's data
 * @prop {string|null=} service_subscription_id - Service Subscription Id
 * @prop {string} organization_id - Organization Id
 * @prop {string|null=} platform_variant - Platform Variant
 * @prop {string} platform_template_id - Platform Template Id
 * @prop {string} url - Url
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {{}=} platform_branding - platform_branding
 * @prop {{}=} platform_config - platform_config
 */
export type addPlatformFormData = {
	service_subscription_id?: string|null;
	organization_id: string;
	platform_variant?: string|null;
	platform_template_id: string;
	url: string;
	status?: Number;
	platform_branding?: {};
	platform_config?: {};
};

/**
 * @typedef {Object} editPlatformFormData Platform's data
 * @prop {string=} id - id of the platform that will be modified. If no value is provided, will create a new entry in platforms
 * @prop {string|null=} service_subscription_id - Service Subscription Id
 * @prop {string=} organization_id - Organization Id
 * @prop {string|null=} platform_variant - Platform Variant
 * @prop {string=} platform_template_id - Platform Template Id
 * @prop {string=} url - Url
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {{}=} platform_branding - platform_branding
 * @prop {{}=} platform_config - platform_config
 */
export type editPlatformFormData = {
	id?: string;
	service_subscription_id?: string|null;
	organization_id?: string;
	platform_variant?: string|null;
	platform_template_id?: string;
	url?: string;
	status?: Number;
	platform_branding?: {};
	platform_config?: {};
};

/**
 * @typedef {Object} listPlaybookStepsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlaybookStepsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlaybookStepFormData Playbook Step's data
 * @prop {string} playbook_id - Playbook Id
 * @prop {Number} step_number - Step Number
 * @prop {string} title - Title
 * @prop {string|null=} description - Description
 * @prop {string|null=} article_id - Article Id
 */
export type addPlaybookStepFormData = {
	playbook_id: string;
	step_number: Number;
	title: string;
	description?: string|null;
	article_id?: string|null;
};

/**
 * @typedef {Object} editPlaybookStepFormData Playbook Step's data
 * @prop {string=} playbook_id - Playbook Id
 * @prop {Number=} step_number - Step Number
 * @prop {string=} title - Title
 * @prop {string|null=} description - Description
 * @prop {string|null=} article_id - Article Id
 */
export type editPlaybookStepFormData = {
	playbook_id?: string;
	step_number?: Number;
	title?: string;
	description?: string|null;
	article_id?: string|null;
};

/**
 * @typedef {Object} addPlaybookFormData Playbook's data
 * @prop {string} name - Name
 * @prop {string} description - Description
 * @prop {Number|null=} hours - Hours
 * @prop {string|null=} client_type - Client Type
 */
export type addPlaybookFormData = {
	name: string;
	description: string;
	hours?: Number|null;
	client_type?: string|null;
};

/**
 * @typedef {Object} editPlaybookFormData Playbook's data
 * @prop {string=} name - Name
 * @prop {string=} description - Description
 * @prop {Number|null=} hours - Hours
 * @prop {string|null=} client_type - Client Type
 */
export type editPlaybookFormData = {
	name?: string;
	description?: string;
	hours?: Number|null;
	client_type?: string|null;
};

/**
 * @typedef {Object} listResourcesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listResourcesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewAllByOrganizationIdResourceFormData
 * @prop {boolean=} group_items - Decision to group items by topic (default true), otherwise, returns data as is
 */
export type viewAllByOrganizationIdResourceFormData = {
	group_items?: boolean;
};

/**
 * @typedef {Object} viewAllForPlatformIDResourceFormData
 * @prop {string=} group_field - Field to group by (topic, category, target_audience)
 */
export type viewAllForPlatformIDResourceFormData = {
	group_field?: string;
};

/**
 * @typedef {Object} addResourceFormData Resource's data
 * @prop {string} organization_id - Organization Id
 * @prop {string} platform_id - Platform Id
 * @prop {string} category - Category
 * @prop {string} topic - Topic
 * @prop {string} target_audience - Target Audience
 * @prop {string} title - Title
 * @prop {string|null=} description - Description
 * @prop {string} url - Url
 * @prop {boolean|null=} published - Published
 * @prop {string|null=} publish_date - Publish Date
 */
export type addResourceFormData = {
	organization_id: string;
	platform_id: string;
	category: string;
	topic: string;
	target_audience: string;
	title: string;
	description?: string|null;
	url: string;
	published?: boolean|null;
	publish_date?: string|null;
};

/**
 * @typedef {Object} editResourceFormData Resource's data
 * @prop {string=} organization_id - Organization Id
 * @prop {string=} platform_id - Platform Id
 * @prop {string=} category - Category
 * @prop {string=} topic - Topic
 * @prop {string=} target_audience - Target Audience
 * @prop {string=} title - Title
 * @prop {string|null=} description - Description
 * @prop {string=} url - Url
 * @prop {boolean|null=} published - Published
 * @prop {string|null=} publish_date - Publish Date
 */
export type editResourceFormData = {
	organization_id?: string;
	platform_id?: string;
	category?: string;
	topic?: string;
	target_audience?: string;
	title?: string;
	description?: string|null;
	url?: string;
	published?: boolean|null;
	publish_date?: string|null;
};

/**
 * @typedef {Object} addSectionFormData Section data
 * @prop {string} section_name - Name of the section name we are creating
 * @prop {boolean} publish_state - Whether the section is published or not (hidden)
 * @prop {Number} sequence_order - Order in the Sequence of Sections (of their respective Page) that this Section needs to appear.
 * @prop {string=} background_color - Background color of the section
 * @prop {string=} background_url - Url or ID of the Background image (to use with SERVICE-SHRD-FM)
 * @prop {string=} background_size - Size of the background
 * @prop {string=} background_position - Position of the background
 * @prop {boolean=} deleted - Whether the section is deleted or not
 * @prop {string=} backgroundmobile_color - Color of the background in mobile
 * @prop {string=} backgroundmobile_url - Url or ID of the Background image (to use with SERVICE-SHRD-FM)
 * @prop {string=} backgroundmobile_size - Size of the background in mobile
 * @prop {string=} backgroundmobile_position - Position of the background in mobile
 */
export type addSectionFormData = {
	section_name: string;
	publish_state: boolean;
	sequence_order: Number;
	background_color?: string;
	background_url?: string;
	background_size?: string;
	background_position?: string;
	deleted?: boolean;
	backgroundmobile_color?: string;
	backgroundmobile_url?: string;
	backgroundmobile_size?: string;
	backgroundmobile_position?: string;
};

/**
 * @typedef {Object} editSectionFormData Section data
 * @prop {string=} section_name - Name of the section name we are creating
 * @prop {boolean=} publish_state - Whether the section is published or not (hidden)
 * @prop {Number=} sequence_order - Order in the Sequence of Sections (of their respective Page) that this Section needs to appear.
 * @prop {string=} background_color - Background color of the section
 * @prop {string=} background_url - Url or ID of the Background image (to use with SERVICE-SHRD-FM)
 * @prop {string=} background_size - Size of the background
 * @prop {string=} background_position - Position of the background
 * @prop {boolean=} deleted - Whether the section is deleted or not
 * @prop {string=} backgroundmobile_color - Color of the background in mobile
 * @prop {string=} backgroundmobile_url - Url or ID of the Background image (to use with SERVICE-SHRD-FM)
 * @prop {string=} backgroundmobile_size - Size of the background in mobile
 * @prop {string=} backgroundmobile_position - Position of the background in mobile
 */
export type editSectionFormData = {
	section_name?: string;
	publish_state?: boolean;
	sequence_order?: Number;
	background_color?: string;
	background_url?: string;
	background_size?: string;
	background_position?: string;
	deleted?: boolean;
	backgroundmobile_color?: string;
	backgroundmobile_url?: string;
	backgroundmobile_size?: string;
	backgroundmobile_position?: string;
};

/**
 * @typedef {Object} listTranslationsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listTranslationsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addTranslationFormData Translation's data
 * @prop {string} slug - Slug
 * @prop {string} locale - Locale
 * @prop {string|null=} platform_variant - Platform Variant
 * @prop {string} value - Value
 * @prop {boolean} new - New
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addTranslationFormData = {
	slug: string;
	locale: string;
	platform_variant?: string|null;
	value: string;
	new: boolean;
	status: Number;
};

/**
 * @typedef {Object} editTranslationFormData Translation's data
 * @prop {string=} slug - Slug
 * @prop {string=} locale - Locale
 * @prop {string|null=} platform_variant - Platform Variant
 * @prop {string=} value - Value
 * @prop {boolean=} new - New
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editTranslationFormData = {
	slug?: string;
	locale?: string;
	platform_variant?: string|null;
	value?: string;
	new?: boolean;
	status?: Number;
};

/**
 * @typedef {Object} convertToSharedTranslationTranslationFormData
 * @prop {string} slug - Slug
 * @prop {string} platform_variant - Platform Variant
 */
export type convertToSharedTranslationTranslationFormData = {
	slug: string;
	platform_variant: string;
};

const service = {
	name: "ECM" as ServiceName,
	version: 1
};

export const useECMApi = () => {
	/**
	 * Call an endpoint on the ECM
	 *
	 * Sets the service to the Request and forwards it to the TengivaApi.call() function
	 * @param {ReactiveString} endpoint - Endpoint to request
	 * @param {RequestOptions} options - Additonal options for the Request
	 * @returns {Promise<any>}
	 */
	const call = async <T = any>(endpoint: ReactiveString, options: RequestOptions) => {
		options = {
			...options,
			service
		};
		return await useTengivaApi<T>(endpoint, options);
	};

	/**
	 * Get list of content
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listArticleContent = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/articlecontent/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new article
	 * 
	 * @param {addArticleContentFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addArticleContent = async <T = any>(formData: addArticleContentFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/articlecontent/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete content.
	 * 
	 * @param {ReactiveString} content - Resolved automatically from the UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteArticleContent = async <T = any>(content: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articlecontent/delete`,
			[
				content
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific article content by article_content id
	 * 
	 * @param {ReactiveString} id - Article Content's UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewArticleContent = async <T = any>(id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articlecontent/view`,
			[
				id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an article content
	 * 
	 * @param {ReactiveString} articlecontent - Resolved automatically from the UUID
	 * @param {editArticleContentFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editArticleContent = async <T = any>(articlecontent: ReactiveString, formData: editArticleContentFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articlecontent/edit`,
			[
				articlecontent
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of articles
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listArticle = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/articles/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of articles of a specific platform
	 * 
	 * @param {ReactiveString} platform_id - UUID the platform
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getListByPlatformArticle = async <T = any>(platform_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articles/getListByPlatform`,
			[
				platform_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Page by Name & Platform
	 * 
	 * @param {ReactiveString} platform_id - Platform ID.
	 * @param {ReactiveString} page_name - Page name.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getArticleByNamePlatformArticle = async <T = any>(platform_id: ReactiveString, page_name: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/articles/getArticleByNamePlatform`,
			[
				platform_id, 
				page_name
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new article
	 * 
	 * @param {addArticleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addArticle = async <T = any>(formData: addArticleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/articles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific article by article id
	 * 
	 * @param {ReactiveString} article - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewArticle = async <T = any>(article: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articles/view`,
			[
				article
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific article by article id
	 * 
	 * @param {ReactiveString} article_page_link - Article's name page link form
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getArticleByNameArticle = async <T = any>(article_page_link: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articles/getArticleByName`,
			[
				article_page_link
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an article
	 * 
	 * @param {ReactiveString} article - Resolved automatically from the UUID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editArticle = async <T = any>(article: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articles/edit`,
			[
				article
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete an article.
	 * 
	 * @param {ReactiveString} article - Resolved automatically from the UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteArticle = async <T = any>(article: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/articles/delete`,
			[
				article
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAuthorizedMerchants = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/authorized_merchants/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} authorizedMerchant - Authorized Merchant id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAuthorizedMerchant = async <T = any>(authorizedMerchant: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_merchants/view`,
			[
				authorizedMerchant
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Platforms details from platform ids array
	 * 
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMarketplaceAuthorizationsForMerchantIdAuthorizedMerchant = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_merchants/viewMarketplaceAuthorizationsForMerchantId`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addAuthorizedMerchantFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addAuthorizedMerchant = async <T = any>(formData: addAuthorizedMerchantFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/authorized_merchants/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} authorizedMerchant - Authorized Merchant id.
	 * @param {editAuthorizedMerchantFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editAuthorizedMerchant = async <T = any>(authorizedMerchant: ReactiveString, formData?: editAuthorizedMerchantFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_merchants/edit`,
			[
				authorizedMerchant
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} authorizedMerchant - Authorized Merchant id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteAuthorizedMerchant = async <T = any>(authorizedMerchant: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_merchants/delete`,
			[
				authorizedMerchant
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listAuthorizedPartnersFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAuthorizedPartners = async <T = any>(formData?: listAuthorizedPartnersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/authorized_partners/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} authorizedPartner - Authorized Partner id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAuthorizedPartner = async <T = any>(authorizedPartner: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_partners/view`,
			[
				authorizedPartner
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addAuthorizedPartnerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addAuthorizedPartner = async <T = any>(formData: addAuthorizedPartnerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/authorized_partners/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} authorizedPartner - Authorized Partner id.
	 * @param {editAuthorizedPartnerFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editAuthorizedPartner = async <T = any>(authorizedPartner: ReactiveString, formData?: editAuthorizedPartnerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_partners/edit`,
			[
				authorizedPartner
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} authorizedPartner - Authorized Partner id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteAuthorizedPartner = async <T = any>(authorizedPartner: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_partners/delete`,
			[
				authorizedPartner
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View platforms for which a partner organization is an authorized partner
	 * 
	 * @param {ReactiveString} partner_organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAuthorizedPlatformsAuthorizedPartner = async <T = any>(partner_organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/authorized_partners/viewAuthorizedPlatforms`,
			[
				partner_organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of content
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listBanners = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/banners/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new banner
	 * 
	 * @param {newBannerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const newBanner = async <T = any>(formData: newBannerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/banners/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific article by article id
	 * 
	 * @param {ReactiveString} banner - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewBanner = async <T = any>(banner: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/banners/view`,
			[
				banner
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit a Banner
	 * 
	 * @param {ReactiveString} banner - Resolved automatically from the UUID
	 * @param {editBannerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editBanner = async <T = any>(banner: ReactiveString, formData: editBannerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/banners/edit`,
			[
				banner
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete a banner.
	 * 
	 * @param {ReactiveString} banner - Resolved automatically from the UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteBanner = async <T = any>(banner: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/banners/delete`,
			[
				banner
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of content
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listBlockItems = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/blockitems/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add new Block Item to a block
	 * 
	 * @param {addBlockItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addBlockItem = async <T = any>(formData: addBlockItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/blockitems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit Block Item
	 * 
	 * @param {ReactiveString} item - Resolved automatically from the UUID
	 * @param {editBlockItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editBlockItem = async <T = any>(item: ReactiveString, formData: editBlockItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/blockitems/edit`,
			[
				item
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete a block item.
	 * 
	 * @param {ReactiveString} item - Resolved automatically from the UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteBlockItem = async <T = any>(item: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/blockitems/delete`,
			[
				item
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of blocks
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listBlocks = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/blocks/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new block
	 * 
	 * @param {ReactiveString} section
	 * @param {addBlockFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addBlock = async <T = any>(section: ReactiveString, formData: addBlockFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/blocks/add`,
			[
				section
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete a block.
	 * 
	 * @param {ReactiveString} block - Resolved automatically from the UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteBlock = async <T = any>(block: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/blocks/delete`,
			[
				block
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit a Block
	 * 
	 * @param {ReactiveString} block - Resolved automatically from the UUID
	 * @param {editBlockFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editBlock = async <T = any>(block: ReactiveString, formData: editBlockFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/blocks/edit`,
			[
				block
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of components
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listComponent = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/components/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific component by component id
	 * 
	 * @param {ReactiveString} component - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewComponent = async <T = any>(component: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/components/view`,
			[
				component
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Component
	 * 
	 * @param {addComponentFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addComponent = async <T = any>(formData: addComponentFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/components/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit
	 * 
	 * @param {ReactiveString} component - Resolved automatically from the UUID
	 * @param {editComponentFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editComponent = async <T = any>(component: ReactiveString, formData: editComponentFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/components/edit`,
			[
				component
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} component - Component id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteComponent = async <T = any>(component: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/components/delete`,
			[
				component
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listComposedElementsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listComposedElements = async <T = any>(formData?: listComposedElementsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/composed_elements/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} composedElement - Composed Element id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewComposedElement = async <T = any>(composedElement: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/composed_elements/view`,
			[
				composedElement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addComposedElementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addComposedElement = async <T = any>(formData: addComposedElementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/composed_elements/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} composedElement - Composed Element id.
	 * @param {editComposedElementFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editComposedElement = async <T = any>(composedElement: ReactiveString, formData?: editComposedElementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/composed_elements/edit`,
			[
				composedElement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} composedElement - Composed Element id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteComposedElement = async <T = any>(composedElement: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/composed_elements/delete`,
			[
				composedElement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of group elements
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listGroupElement = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/groupelements/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific group element by group element id
	 * 
	 * @param {ReactiveString} group_element - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewGroupElement = async <T = any>(group_element: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/groupelements/view`,
			[
				group_element
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Group Element
	 * 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addGroupElement = async <T = any>(formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/groupelements/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit
	 * 
	 * @param {ReactiveString} group_element - Resolved automatically from the UUID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editGroupElement = async <T = any>(group_element: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/groupelements/edit`,
			[
				group_element
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of articles
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listImageCollection = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/imagecollections/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Image Collection
	 * 
	 * @param {addImageCollectionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addImageCollection = async <T = any>(formData: addImageCollectionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/imagecollections/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific image collection by imageCollection id
	 * 
	 * @param {ReactiveString} imageCollection - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewImageCollection = async <T = any>(imageCollection: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/imagecollections/view`,
			[
				imageCollection
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an ImageCollection
	 * 
	 * @param {ReactiveString} imageCollection - Resolved automatically from the UUID
	 * @param {editImageCollectionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editImageCollection = async <T = any>(imageCollection: ReactiveString, formData: editImageCollectionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/imagecollections/edit`,
			[
				imageCollection
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listImageReferences = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/imagereferences/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} imageReference - Image Reference id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewImageReference = async <T = any>(imageReference: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/imagereferences/view`,
			[
				imageReference
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addImageReferenceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addImageReference = async <T = any>(formData: addImageReferenceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/imagereferences/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} imageReference - Image Reference id.
	 * @param {editImageReferenceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editImageReference = async <T = any>(imageReference: ReactiveString, formData: editImageReferenceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/imagereferences/edit`,
			[
				imageReference
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} imageReference - Image Reference id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteImageReference = async <T = any>(imageReference: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/imagereferences/delete`,
			[
				imageReference
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of languages
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listLanguage = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/languages/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Language
	 * 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLanguage = async <T = any>(formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/languages/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific platform template by id
	 * 
	 * @param {ReactiveString} language - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLanguage = async <T = any>(language: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/languages/view`,
			[
				language
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit a Language
	 * 
	 * @param {ReactiveString} language - Resolved automatically from the UUID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLanguage = async <T = any>(language: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/languages/edit`,
			[
				language
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Retrieve platforms (sales channels/marketplaces) where organization_id
	 * is to be used as the merchant_organization
	 * 
	 * @param {ReactiveString} organization_id - organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAvailableForOrganizationIdMarketplace = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listAvailableForOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Retrieve active marketplaces (sales channels/marketplaces)
	 * 
	 * @param {ReactiveString} organization_id - organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listActiveMarketplace = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listActive`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Retrieve active marketplaces (sales channels/marketplaces)
	 * 
	 * @param {ReactiveString} organization_id - organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMerchantPendingApprovalsMarketplace = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listMerchantPendingApprovals`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * add entry for this organization in authorized_merchants of the ECM.
	 * 
	 * @param {makeRequestOrInvitationMarketplaceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const makeRequestOrInvitationMarketplace = async <T = any>(formData: makeRequestOrInvitationMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/marketplaces/makeRequestOrInvitation`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * listMerchantsForMarketplace function - List all merchants that are not yet in authorized_merchants for a platform_id
	 * These merchants can be available to sell on this marketplace (platform_id)
	 * Response will be an array of objects where each object contains merchant's id, profile.public_name, profile.description and online.website in case subscription_platform variant is Showroom-Merchant.
	 * 
	 * @param {ReactiveString} platform_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMerchantsForMarketplaceMarketplace = async <T = any>(platform_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listMerchantsForMarketplace`,
			[
				platform_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * listPendingMerchantsApprovals function - List requests received and invitations made by a marketplace platform whose status is Pending
	 * To access the results the requestor should have SuperAdmin, Marketing or Products role or Tengiva Maintainer role
	 * 
	 * merchant_data key is added to hold Merchant's organization profile data
	 * like public_name and description and online.website in case subscription_platform variant is Showroom-Merchant.
	 * If merchant_organization_id is not found at OrgM, it is set to null
	 * The results are paginated and filters can be set through URL query parameters
	 * 
	 * @param {ReactiveString} platform_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPendingMerchantsApprovalsMarketplace = async <T = any>(platform_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listPendingMerchantsApprovals`,
			[
				platform_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * listActiveMerchants function - List requests received and invitations made by a marketplace platform whose status is Active
	 * To access the results the requestor should have SuperAdmin, Marketing or Products role or Tengiva Maintainer role
	 * 
	 * merchant_data key is added to hold Merchant's organization profile data
	 * like public_name and description and online.website in case subscription_platform variant is Showroom-Merchant.
	 * If merchant_organization_id is not found at OrgM, it is set to null
	 * The results are paginated and filters can be set through URL query parameters
	 * 
	 * @param {ReactiveString} platform_id
	 * @param {listActiveMerchantsMarketplaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listActiveMerchantsMarketplace = async <T = any>(platform_id: ReactiveString, formData?: listActiveMerchantsMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listActiveMerchants`,
			[
				platform_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * updateRequestOrInvitation - Update status of requests received or invitations made by a marketplace platform by id
	 * To modify this record the requestor should have SuperAdmin, Marketing or Products role or Tengiva Maintainer role
	 * 
	 * @param {ReactiveString} id
	 * @param {updateRequestOrInvitationMarketplaceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const updateRequestOrInvitationMarketplace = async <T = any>(id: ReactiveString, formData: updateRequestOrInvitationMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/updateRequestOrInvitation`,
			[
				id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of navigation groups
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listNavigationGroup = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/navigationgroups/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific navigation by navigation id
	 * 
	 * @param {ReactiveString} navigation_group - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewNavigationGroup = async <T = any>(navigation_group: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/navigationgroups/view`,
			[
				navigation_group
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Navigation
	 * 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addNavigationGroup = async <T = any>(formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/navigationgroups/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit
	 * 
	 * @param {ReactiveString} navigation_group - Resolved automatically from the UUID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editNavigationGroup = async <T = any>(navigation_group: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/navigationgroups/edit`,
			[
				navigation_group
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of navigations
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listNavigation = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/navigations/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of navigations by platform template
	 * 
	 * @param {ReactiveString} template_item
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listbytemplateitemNavigation = async <T = any>(template_item: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/navigations/listbytemplateitem`,
			[
				template_item
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific navigation by navigation id
	 * 
	 * @param {ReactiveString} navigation - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewNavigation = async <T = any>(navigation: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/navigations/view`,
			[
				navigation
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Navigation
	 * 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addNavigation = async <T = any>(formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/navigations/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit
	 * 
	 * @param {ReactiveString} navigation - Resolved automatically from the UUID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editNavigation = async <T = any>(navigation: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/navigations/edit`,
			[
				navigation
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPageMetasFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPageMetas = async <T = any>(formData: listPageMetasFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/page_metas/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} pageMeta - Page Meta id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPageMeta = async <T = any>(pageMeta: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/page_metas/view`,
			[
				pageMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPageMetaFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPageMeta = async <T = any>(formData: addPageMetaFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/page_metas/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} pageMeta - Page Meta id.
	 * @param {editPageMetaFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPageMeta = async <T = any>(pageMeta: ReactiveString, formData?: editPageMetaFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/page_metas/edit`,
			[
				pageMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} pageMeta - Page Meta id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePageMeta = async <T = any>(pageMeta: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/page_metas/delete`,
			[
				pageMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPageStatesFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPageStates = async <T = any>(formData: listPageStatesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/page_states/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} pageState - Page State id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPageState = async <T = any>(pageState: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/page_states/view`,
			[
				pageState
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPageStateFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPageState = async <T = any>(formData: addPageStateFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/page_states/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} pageState - Page State id.
	 * @param {editPageStateFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPageState = async <T = any>(pageState: ReactiveString, formData?: editPageStateFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/page_states/edit`,
			[
				pageState
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} pageState - Page State id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePageState = async <T = any>(pageState: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/page_states/delete`,
			[
				pageState
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPagesFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPages = async <T = any>(formData: listPagesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pages/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {ReactiveString} platform_id - Platform
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const pagesByPlatformPage = async <T = any>(platform_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pages/pagesByPlatform`,
			[
				platform_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} page - Page id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPage = async <T = any>(page: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pages/view`,
			[
				page
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Page by Name & Platform
	 * 
	 * @param {ReactiveString} platform_id - Platform ID.
	 * @param {ReactiveString} page_name - Page name.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPageByNamePlatformPage = async <T = any>(platform_id: ReactiveString, page_name: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pages/getPageByNamePlatform`,
			[
				platform_id, 
				page_name
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPageFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPage = async <T = any>(formData: addPageFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pages/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} page - Page id.
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPage = async <T = any>(page: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pages/edit`,
			[
				page
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} page - Page id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePage = async <T = any>(page: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pages/delete`,
			[
				page
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformBrandingsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformBrandings = async <T = any>(formData: listPlatformBrandingsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_brandings/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformBranding - Platform Branding id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformBranding = async <T = any>(platformBranding: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_brandings/view`,
			[
				platformBranding
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformBrandingFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformBranding = async <T = any>(formData: addPlatformBrandingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_brandings/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformBranding - Platform Branding id.
	 * @param {editPlatformBrandingFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformBranding = async <T = any>(platformBranding: ReactiveString, formData?: editPlatformBrandingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_brandings/edit`,
			[
				platformBranding
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformBranding - Platform Branding id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformBranding = async <T = any>(platformBranding: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_brandings/delete`,
			[
				platformBranding
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformColorsetsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformColorsets = async <T = any>(formData: listPlatformColorsetsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_colorsets/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformColorset - Platform Colorset id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformColorset = async <T = any>(platformColorset: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_colorsets/view`,
			[
				platformColorset
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformColorsetFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformColorset = async <T = any>(formData: addPlatformColorsetFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_colorsets/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformColorset - Platform Colorset id.
	 * @param {editPlatformColorsetFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformColorset = async <T = any>(platformColorset: ReactiveString, formData?: editPlatformColorsetFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_colorsets/edit`,
			[
				platformColorset
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformColorset - Platform Colorset id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformColorset = async <T = any>(platformColorset: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_colorsets/delete`,
			[
				platformColorset
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformConfigsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformConfigs = async <T = any>(formData: listPlatformConfigsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_configs/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformConfig - Platform Config id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformConfig = async <T = any>(platformConfig: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_configs/view`,
			[
				platformConfig
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View by platform id method
	 * 
	 * @param {ReactiveString} platformId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForPlatformIdPlatformConfig = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_configs/viewForPlatformId`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformConfigFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformConfig = async <T = any>(formData: addPlatformConfigFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_configs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformConfig - Platform Config id.
	 * @param {editPlatformConfigFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformConfig = async <T = any>(platformConfig: ReactiveString, formData?: editPlatformConfigFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_configs/edit`,
			[
				platformConfig
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformConfig - Platform Config id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformConfig = async <T = any>(platformConfig: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_configs/delete`,
			[
				platformConfig
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getPlatformNamesFromIds function - Get platform names for the requested array of platform_id-s
	 * 
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPlatformNamesFromIdsPlatformConfig = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/platform_configs/getPlatformNamesFromIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformFontsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformFonts = async <T = any>(formData: listPlatformFontsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_fonts/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformFont - Platform Font id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformFont = async <T = any>(platformFont: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_fonts/view`,
			[
				platformFont
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformFontFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformFont = async <T = any>(formData: addPlatformFontFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_fonts/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformFont - Platform Font id.
	 * @param {editPlatformFontFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformFont = async <T = any>(platformFont: ReactiveString, formData?: editPlatformFontFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_fonts/edit`,
			[
				platformFont
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformFont - Platform Font id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformFont = async <T = any>(platformFont: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_fonts/delete`,
			[
				platformFont
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformInputsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformInputs = async <T = any>(formData: listPlatformInputsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_inputs/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformInput - Platform Input id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformInput = async <T = any>(platformInput: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_inputs/view`,
			[
				platformInput
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformInputFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformInput = async <T = any>(formData: addPlatformInputFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_inputs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformInput - Platform Input id.
	 * @param {editPlatformInputFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformInput = async <T = any>(platformInput: ReactiveString, formData?: editPlatformInputFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_inputs/edit`,
			[
				platformInput
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformInput - Platform Input id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformInput = async <T = any>(platformInput: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_inputs/delete`,
			[
				platformInput
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of platform templates
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformTemplate = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/platformtemplates/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new Platform template
	 * 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformTemplate = async <T = any>(formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/platformtemplates/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific platform template by  i
	 * 
	 * @param {ReactiveString} template - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformTemplate = async <T = any>(template: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platformtemplates/view`,
			[
				template
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit a Platform template
	 * 
	 * @param {ReactiveString} template - Resolved automatically from the UUID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformTemplate = async <T = any>(template: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platformtemplates/edit`,
			[
				template
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatforms = async <T = any>(formData: listPlatformsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platforms/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platform - Platform id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatform = async <T = any>(platform: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platforms/view`,
			[
				platform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platform - Platform id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPlatformEmailConfigsPlatform = async <T = any>(platform: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platforms/getPlatformEmailConfigs`,
			[
				platform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get platform setting by Organization id
	 * 
	 * @param {ReactiveString} organization_id - Platform Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPlatformSettingsPlatform = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platforms/getPlatformSettings`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get platform setting by url
	 * 
	 * @param {ReactiveString} url - Platform url
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPlatformSettingsFromURLPlatform = async <T = any>(url: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platforms/getPlatformSettingsFromURL`,
			[
				url
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific platform by platform id
	 * 
	 * @param {ReactiveString} platform_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformsFromSystemPlatform = async <T = any>(platform_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms/viewPlatformsForSystem`,
			[
				platform_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Platform matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdPlatform = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Platforms details by organization id.
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getSummaryByOrganizationIdPlatform = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms/getSummaryByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Platform matching an Organization ID
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getOrganizationPlatformsDetailsForSystemPlatform = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms/getOrganizationPlatformsDetailsForSystem`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Platforms details from platform ids array
	 * 
	 * If we eventually want to only return the system menu appropriate for the onboard status
	 * it will need to ne added to the request body and the logic would need to be implemented.
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPlatformsDetailsbyIdsPlatform = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/platforms/getPlatformsDetailsbyIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Platforms details from platform ids array
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getMarketplaceSummaryForPlatformIdsPlatform = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/platforms/getMarketplaceSummaryForPlatformIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatform = async <T = any>(formData: addPlatformFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platforms/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platform - Platform id.
	 * @param {editPlatformFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatform = async <T = any>(platform: ReactiveString, formData?: editPlatformFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms/edit`,
			[
				platform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platform - Platform id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatform = async <T = any>(platform: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms/delete`,
			[
				platform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlaybookStepsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlaybookSteps = async <T = any>(formData: listPlaybookStepsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/playbook_steps/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} playbookStep - Playbook Step id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlaybookStep = async <T = any>(playbookStep: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/playbook_steps/view`,
			[
				playbookStep
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlaybookStepFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlaybookStep = async <T = any>(formData: addPlaybookStepFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/playbook_steps/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} playbookStep - Playbook Step id.
	 * @param {editPlaybookStepFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlaybookStep = async <T = any>(playbookStep: ReactiveString, formData?: editPlaybookStepFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/playbook_steps/edit`,
			[
				playbookStep
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} playbookStep - Playbook Step id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlaybookStep = async <T = any>(playbookStep: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/playbook_steps/delete`,
			[
				playbookStep
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlaybooks = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/playbooks/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} playbook - Playbook id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlaybook = async <T = any>(playbook: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/playbooks/view`,
			[
				playbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlaybookFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlaybook = async <T = any>(formData: addPlaybookFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/playbooks/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} playbook - Playbook id.
	 * @param {editPlaybookFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlaybook = async <T = any>(playbook: ReactiveString, formData?: editPlaybookFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/playbooks/edit`,
			[
				playbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} playbook - Playbook id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlaybook = async <T = any>(playbook: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/playbooks/delete`,
			[
				playbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listResourcesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listResources = async <T = any>(formData?: listResourcesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/resources/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} resource - Resource id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewResource = async <T = any>(resource: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/resources/view`,
			[
				resource
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Resource matching an Organization ID - For Display usage in MKTPLC
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {viewAllByOrganizationIdResourceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdResource = async <T = any>(organization_id: ReactiveString, formData?: viewAllByOrganizationIdResourceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/resources/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Resource to be displayed for a Platform ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} platform_id - Platform
	 * @param {viewAllForPlatformIDResourceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllForPlatformIDResource = async <T = any>(platform_id: ReactiveString, formData?: viewAllForPlatformIDResourceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/resources/viewAllForPlatformID`,
			[
				platform_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addResourceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addResource = async <T = any>(formData: addResourceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/resources/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} resource - Resource id.
	 * @param {editResourceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editResource = async <T = any>(resource: ReactiveString, formData?: editResourceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/resources/edit`,
			[
				resource
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} resource - Resource id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteResource = async <T = any>(resource: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/resources/delete`,
			[
				resource
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of sections
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSections = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/sections/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new section
	 * 
	 * @param {ReactiveString} page
	 * @param {addSectionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSection = async <T = any>(page: ReactiveString, formData: addSectionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/sections/add`,
			[
				page
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete a section.
	 * 
	 * @param {ReactiveString} section - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteSection = async <T = any>(section: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/sections/delete`,
			[
				section
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit
	 * 
	 * @param {ReactiveString} section - Resolved automatically from the UUID
	 * @param {editSectionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editSection = async <T = any>(section: ReactiveString, formData: editSectionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/sections/edit`,
			[
				section
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of TemplateItems
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listTemplateItem = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/templateitems/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a new TemplateItem
	 * 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTemplateItem = async <T = any>(formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/templateitems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific TemplateItem by id
	 * 
	 * @param {ReactiveString} item - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTemplateItem = async <T = any>(item: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/templateitems/view`,
			[
				item
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit a TemplateItem
	 * 
	 * @param {ReactiveString} item - Resolved automatically from the UUID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTemplateItem = async <T = any>(item: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/templateitems/edit`,
			[
				item
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get a list of translations
	 * 
	 * Will always return the english (master) list of active strings to translates.
	 * When a locale is specified in the filters, each english object will
	 * contain a "TranslationsLocalized" object containing the translation
	 * These localized strings may be inactive/deleted (status <> 1),
	 * meaning they will not be exported to the Frontend unless re-activated.
	 * If there is no localized translation, it will need to be create.
	 * 
	 * @param {listTranslationsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listTranslations = async <T = any>(formData: listTranslationsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/translations/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Fetch the english translation files from the GCP Bucket & update the database
	 * 
	 * Will insert any new strings needing translations
	 * and set the status of any deleted strings to deleted
	 * By default, it will check every platform
	 * If a platform variant is specified, only that platform will be checked
	 * 
	 * @param {ReactiveString} platform_variant - 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const checkDiffsTranslation = async <T = any>(platform_variant: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/translations/checkDiffs`,
			[
				platform_variant
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Sync the translations from the database to the Cloud Bucket
	 * 
	 * This will only upload to the GCP Bucket when it is run in production
	 * When called in dev or locally, you must specify the platform_name & locale in the URL params
	 * to see the results as the method's response.
	 * If you don't specify a platform name or locale, no results will be visible
	 * By default, it will create the file for every platform & locale
	 * If a platform variant is specified, only that platform will be done
	 * If a locale is specified, only that locale will be done
	 * If a platform variant & locale are specified, only that platform & locale will be done
	 * 
	 * @param {ReactiveString} platform_variant - 
	 * @param {ReactiveString} locale - 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const syncReposTranslation = async <T = any>(platform_variant: ReactiveString, locale: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/translations/syncRepos`,
			[
				platform_variant, 
				locale
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} translation - Translation id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTranslation = async <T = any>(translation: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/translations/view`,
			[
				translation
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addTranslationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTranslation = async <T = any>(formData: addTranslationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/translations/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} translation - Translation id.
	 * @param {editTranslationFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTranslation = async <T = any>(translation: ReactiveString, formData?: editTranslationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/translations/edit`,
			[
				translation
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * !Edit method
	 * 
	 * !param \App\Model\Entity\Translation $translation Translation id.
	 * 
	 * @param {convertToSharedTranslationTranslationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const convertToSharedTranslationTranslation = async <T = any>(formData: convertToSharedTranslationTranslationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/translations/convertToSharedTranslation`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} translation - Translation id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTranslation = async <T = any>(translation: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/translations/delete`,
			[
				translation
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};


	return {
		call,
		listArticleContent,
		addArticleContent,
		deleteArticleContent,
		viewArticleContent,
		editArticleContent,
		listArticle,
		getListByPlatformArticle,
		getArticleByNamePlatformArticle,
		addArticle,
		viewArticle,
		getArticleByNameArticle,
		editArticle,
		deleteArticle,
		listAuthorizedMerchants,
		viewAuthorizedMerchant,
		viewMarketplaceAuthorizationsForMerchantIdAuthorizedMerchant,
		addAuthorizedMerchant,
		editAuthorizedMerchant,
		deleteAuthorizedMerchant,
		listAuthorizedPartners,
		viewAuthorizedPartner,
		addAuthorizedPartner,
		editAuthorizedPartner,
		deleteAuthorizedPartner,
		viewAuthorizedPlatformsAuthorizedPartner,
		listBanners,
		newBanner,
		viewBanner,
		editBanner,
		deleteBanner,
		listBlockItems,
		addBlockItem,
		editBlockItem,
		deleteBlockItem,
		listBlocks,
		addBlock,
		deleteBlock,
		editBlock,
		listComponent,
		viewComponent,
		addComponent,
		editComponent,
		deleteComponent,
		listComposedElements,
		viewComposedElement,
		addComposedElement,
		editComposedElement,
		deleteComposedElement,
		listGroupElement,
		viewGroupElement,
		addGroupElement,
		editGroupElement,
		listImageCollection,
		addImageCollection,
		viewImageCollection,
		editImageCollection,
		listImageReferences,
		viewImageReference,
		addImageReference,
		editImageReference,
		deleteImageReference,
		listLanguage,
		addLanguage,
		viewLanguage,
		editLanguage,
		listAvailableForOrganizationIdMarketplace,
		listActiveMarketplace,
		listMerchantPendingApprovalsMarketplace,
		makeRequestOrInvitationMarketplace,
		listMerchantsForMarketplaceMarketplace,
		listPendingMerchantsApprovalsMarketplace,
		listActiveMerchantsMarketplace,
		updateRequestOrInvitationMarketplace,
		listNavigationGroup,
		viewNavigationGroup,
		addNavigationGroup,
		editNavigationGroup,
		listNavigation,
		listbytemplateitemNavigation,
		viewNavigation,
		addNavigation,
		editNavigation,
		listPageMetas,
		viewPageMeta,
		addPageMeta,
		editPageMeta,
		deletePageMeta,
		listPageStates,
		viewPageState,
		addPageState,
		editPageState,
		deletePageState,
		listPages,
		pagesByPlatformPage,
		viewPage,
		getPageByNamePlatformPage,
		addPage,
		editPage,
		deletePage,
		listPlatformBrandings,
		viewPlatformBranding,
		addPlatformBranding,
		editPlatformBranding,
		deletePlatformBranding,
		listPlatformColorsets,
		viewPlatformColorset,
		addPlatformColorset,
		editPlatformColorset,
		deletePlatformColorset,
		listPlatformConfigs,
		viewPlatformConfig,
		viewForPlatformIdPlatformConfig,
		addPlatformConfig,
		editPlatformConfig,
		deletePlatformConfig,
		getPlatformNamesFromIdsPlatformConfig,
		listPlatformFonts,
		viewPlatformFont,
		addPlatformFont,
		editPlatformFont,
		deletePlatformFont,
		listPlatformInputs,
		viewPlatformInput,
		addPlatformInput,
		editPlatformInput,
		deletePlatformInput,
		listPlatformTemplate,
		addPlatformTemplate,
		viewPlatformTemplate,
		editPlatformTemplate,
		listPlatforms,
		viewPlatform,
		getPlatformEmailConfigsPlatform,
		getPlatformSettingsPlatform,
		getPlatformSettingsFromURLPlatform,
		viewPlatformsFromSystemPlatform,
		viewAllByOrganizationIdPlatform,
		getSummaryByOrganizationIdPlatform,
		getOrganizationPlatformsDetailsForSystemPlatform,
		getPlatformsDetailsbyIdsPlatform,
		getMarketplaceSummaryForPlatformIdsPlatform,
		addPlatform,
		editPlatform,
		deletePlatform,
		listPlaybookSteps,
		viewPlaybookStep,
		addPlaybookStep,
		editPlaybookStep,
		deletePlaybookStep,
		listPlaybooks,
		viewPlaybook,
		addPlaybook,
		editPlaybook,
		deletePlaybook,
		listResources,
		viewResource,
		viewAllByOrganizationIdResource,
		viewAllForPlatformIDResource,
		addResource,
		editResource,
		deleteResource,
		listSections,
		addSection,
		deleteSection,
		editSection,
		listTemplateItem,
		addTemplateItem,
		viewTemplateItem,
		editTemplateItem,
		listTranslations,
		checkDiffsTranslation,
		syncReposTranslation,
		viewTranslation,
		addTranslation,
		editTranslation,
		convertToSharedTranslationTranslation,
		deleteTranslation,
	}
};